import { Breadcrumbs } from '@/components/Breadcrumbs';
import {
  CONTENT_CLASSNAME,
  CONTENT_SPACE_CLASSNAME,
  EMPTY_META,
  FRONT_URL,
} from '@/constants';
import { Layout } from '@/layout';
import { InferGetServerSidePropsType } from 'next';
import styles from './Сertificates.module.scss';
import { Title } from '@/components/Title';
import { DocumentLink } from '@/components/DocumentLink';
import { CertificateType, Palettes, Variants } from '@/types';
import { Divider } from '@/components/Divider';
import { fetchData } from '@/services/fetchData';
import { getBackendImage } from '@/utils/getBackendImage';
import { useTranslations } from '@/hooks/useTranslations';
import { BannerStatic } from '@/components/BannerStatic';
import { useAdaptiveLayout } from '@/hooks/useAdaptiveLayout';
import Head from 'next/head';
import { Button } from '@/components/Button';
import { useClientTranslations } from '@/hooks/useClientTranslations';
import { useState } from 'react';

export default function Certificates({
  content,
  description,
  seo,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const { getTranslations } = useTranslations();
  const { isTablet } = useAdaptiveLayout();
  const t = useClientTranslations();

  const [shownCount, setShownCount] = useState(10);

  const handleShowMore = () => {
    setShownCount(shownCount + 10);
  };

  return (
    <>
      <Head>
        <link rel="canonical" href={`${FRONT_URL}/certificates`} />
      </Head>
      <Layout meta={seo}>
        <main className={styles.main}>
          <Breadcrumbs
            routes={[
              {
                link: '/certificates',
                text: getTranslations(description.title),
              },
            ]}
          />
          <Title
            text={getTranslations(description.title)}
            className={styles.title}
            heading
          />
          <div className={styles.content}>
            <div className={styles.column}>
              {content
                .slice(0, shownCount)
                .map((item: CertificateType, index: number) => (
                  <>
                    <DocumentLink
                      key={item.id}
                      title={getTranslations(item.name)}
                      link={getBackendImage(item.file)}
                    />
                    {index !== content.length - 1 && <Divider />}
                  </>
                ))}
              {content.length > shownCount && (
                <Button
                  text={t.certificates.button}
                  palette={Palettes.Transparent}
                  variant={Variants.Outline}
                  onClick={handleShowMore}
                  className={styles.button}
                />
              )}
            </div>
            <div
              className={isTablet ? CONTENT_SPACE_CLASSNAME : CONTENT_CLASSNAME}
              dangerouslySetInnerHTML={{
                __html: getTranslations(description.body),
              }}
            />
            {description.mediaFile && (
              <div className={CONTENT_CLASSNAME}>
                <BannerStatic
                  image={description.mediaFile}
                  mobileImage={description.mobileMediaFile}
                  tabletImage={description.tabletMediaFile}
                />
              </div>
            )}
          </div>
        </main>
      </Layout>
    </>
  );
}

export async function getServerSideProps() {
  const listRes = await fetchData({ url: '/certificate/list?limit=1000' });
  const descriptionRes = await fetchData({
    url: '/descriptionBlock/page/sertificates',
  });

  const seoRes = await fetchData({
    url: '/seo/page/sertificates',
  });

  if (!listRes.success || !descriptionRes.success) return { notFound: true };

  return {
    props: {
      content: listRes.data.results,
      description: descriptionRes.data,
      seo: seoRes.data ?? EMPTY_META,
    },
  };
}
